import { useState, useEffect } from 'react'
import { Bell, Mail, Smartphone, Settings, Save } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { doc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '../services/firebase'
import toast from 'react-hot-toast'

export default function NotificationSettings() {
  const { currentUser, userProfile } = useAuth()
  const [preferences, setPreferences] = useState({
    pushNotifications: true,
    emailNotifications: true,
    alertEmails: true,
    groupEmails: true,
    organizationEmails: true
  })
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (userProfile?.notificationPreferences) {
      setPreferences(userProfile.notificationPreferences)
    }
  }, [userProfile])

  const handlePreferenceChange = async (key) => {
    const newPreferences = {
      ...preferences,
      [key]: !preferences[key]
    }
    
    setPreferences(newPreferences)
    
    // Auto-save immediately when toggled
    if (currentUser) {
      try {
        // Save to main user document
        await updateDoc(doc(db, 'users', currentUser.uid), {
          notificationPreferences: newPreferences,
          updatedAt: serverTimestamp()
        })
        
        // Also save to subcollection
        await setDoc(doc(db, 'users', currentUser.uid, 'preferences', 'notifications'), {
          ...newPreferences,
          updatedAt: serverTimestamp(),
          userId: currentUser.uid
        })
        
        console.log(`✅ Auto-saved notification preference: ${key} = ${newPreferences[key]}`)
      } catch (error) {
        console.error('Error auto-saving notification preference:', error)
        toast.error('Failed to save preference')
      }
    }
  }

  const handleSave = async () => {
    if (!currentUser) return

    try {
      setSaving(true)
      
      // Save to main user document
      await updateDoc(doc(db, 'users', currentUser.uid), {
        notificationPreferences: preferences,
        updatedAt: serverTimestamp()
      })
      
      // Also save to subcollection for better organization
      await setDoc(doc(db, 'users', currentUser.uid, 'preferences', 'notifications'), {
        ...preferences,
        updatedAt: serverTimestamp(),
        userId: currentUser.uid
      })
      
      console.log('✅ Notification preferences saved to both user document and subcollection')
      toast.success('Notification preferences saved!')
    } catch (error) {
      console.error('Error saving notification preferences:', error)
      toast.error('Failed to save preferences')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <Bell className="h-6 w-6 text-primary-600" />
        <h2 className="text-xl font-semibold text-gray-900">Notification Settings</h2>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
        {/* Push Notifications */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Smartphone className="h-5 w-5 text-gray-500" />
            <div>
              <h3 className="text-sm font-medium text-gray-900">Push Notifications</h3>
              <p className="text-sm text-gray-500">Receive push notifications on your device</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={preferences.pushNotifications}
              onChange={() => handlePreferenceChange('pushNotifications')}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>

        {/* Email Notifications */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Mail className="h-5 w-5 text-gray-500" />
            <div>
              <h3 className="text-sm font-medium text-gray-900">Email Notifications</h3>
              <p className="text-sm text-gray-500">Receive email notifications</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={preferences.emailNotifications}
              onChange={() => handlePreferenceChange('emailNotifications')}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>

        {/* Alert Emails */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Bell className="h-5 w-5 text-gray-500" />
            <div>
              <h3 className="text-sm font-medium text-gray-900">Alert Emails</h3>
              <p className="text-sm text-gray-500">Receive emails for new alerts</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={preferences.alertEmails}
              onChange={() => handlePreferenceChange('alertEmails')}
              disabled={!preferences.emailNotifications}
              className="sr-only peer"
            />
            <div className={`w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 ${
              !preferences.emailNotifications ? 'bg-gray-100' : 'bg-gray-200'
            }`}></div>
          </label>
        </div>

        {/* Group Emails */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Settings className="h-5 w-5 text-gray-500" />
            <div>
              <h3 className="text-sm font-medium text-gray-900">Group Emails</h3>
              <p className="text-sm text-gray-500">Receive emails for group-specific alerts</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={preferences.groupEmails}
              onChange={() => handlePreferenceChange('groupEmails')}
              disabled={!preferences.emailNotifications}
              className="sr-only peer"
            />
            <div className={`w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 ${
              !preferences.emailNotifications ? 'bg-gray-100' : 'bg-gray-200'
            }`}></div>
          </label>
        </div>

        {/* Organization Emails */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Settings className="h-5 w-5 text-gray-500" />
            <div>
              <h3 className="text-sm font-medium text-gray-900">Organization Emails</h3>
              <p className="text-sm text-gray-500">Receive emails for organization-wide alerts</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={preferences.organizationEmails}
              onChange={() => handlePreferenceChange('organizationEmails')}
              disabled={!preferences.emailNotifications}
              className="sr-only peer"
            />
            <div className={`w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 ${
              !preferences.emailNotifications ? 'bg-gray-100' : 'bg-gray-200'
            }`}></div>
          </label>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary flex items-center space-x-2"
        >
          <Save className="h-4 w-4" />
          <span>{saving ? 'Saving...' : 'Save Preferences'}</span>
        </button>
      </div>
    </div>
  )
}
